import { Sparkle, Truck } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { H4, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Flag } from '@/components/Flag/Flag';
import SearchSort from '@/components/SearchResults/SearchSort';
import { useAppSelector } from '@/redux/hooks';
import { getDiscoverPaginationRecordsCount } from '@/redux/modules/discover';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { toQueryString } from '@/utils/urls';

import normalizeQuery from './helpers/normalizeQuery';

const DEFAULT_PAGE_SORT = SearchSortAndDirection.MostRelevant;

const SORT_OPTIONS = [
    SearchSortAndDirection.MostRelevant,
    SearchSortAndDirection.NewlyListed,
    SearchSortAndDirection.TimeMostRecent,
    SearchSortAndDirection.MostBids,
    SearchSortAndDirection.EstimateHighest,
    SearchSortAndDirection.PriceHighest,
    SearchSortAndDirection.LotNumberLowest,
];

interface QueryParams {
    country?: string;
    publishDateMin?: string;
    shipping?: string;
    sort?: SearchSortAndDirection;
    topRated?: string;
}

export const DiscoverPageHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectId = useId();
    const formatMessage = useAppFormatMessage();
    const totalRecords = useAppSelector(getDiscoverPaginationRecordsCount);
    const params: QueryParams = parseQueryParams(location.search);
    const { country, publishDateMin, shipping, sort, topRated } = params;

    const navigateTo = useCallback(
        (param: QueryParams) => {
            const newParams = normalizeQuery({ ...params, ...param });
            navigate({ search: toQueryString(newParams) });
        },
        [navigate, params]
    );

    const handleSortUpdate = useCallback((sort: SearchSortAndDirection) => navigateTo({ sort }), [navigateTo]);

    const handleShippingFilter = useCallback(
        () =>
            navigateTo({
                shipping: shipping === 'inHouseShipping' ? '' : 'inHouseShipping',
            }),
        [navigateTo, shipping]
    );

    const handleUsFilter = useCallback(
        () => navigateTo({ country: country === 'US' ? '' : 'US' }),
        [navigateTo, country]
    );

    const handleNewFilter = useCallback(() => {
        const twoDaysAgo = Math.floor((Date.now() - 172800000) / 1000);
        navigateTo({
            publishDateMin: publishDateMin ? '' : `${twoDaysAgo}`,
        });
    }, [navigateTo, publishDateMin]);

    const handleTopRatedFilter = useCallback(
        () => navigateTo({ topRated: topRated === 'topRated' ? '' : 'topRated' }),
        [navigateTo, topRated]
    );

    return (
        <>
            <div className="flex flex-row items-center justify-start overflow-x-auto overflow-y-hidden px-0 py-[30px] xsMax:py-[15px]">
                <div
                    className={cn(
                        'bg-white mr-xs flex h-[42px] cursor-pointer flex-row items-center justify-between border border-border-secondary px-[15px] whitespace-nowrap text-text-secondary transition-all duration-[250ms]',
                        'xsMax:h-[30px] xsMax:px-xs',
                        Boolean(shipping) && 'bg-border-secondary'
                    )}
                    onClick={handleShippingFilter}
                >
                    <Truck
                        className="mr-[12px] h-[24px] w-[24px]"
                        style="fill"
                    />
                    <H6>{formatMessage(['discoverPage', 'buttons', 'shipping'])}</H6>
                </div>
                <div
                    className={cn(
                        'bg-white mr-xs flex h-[42px] cursor-pointer flex-row items-center justify-between border border-border-secondary px-[15px] whitespace-nowrap text-text-secondary transition-all duration-[250ms]',
                        'xsMax:h-[30px] xsMax:px-xs',
                        Boolean(country) && 'bg-border-secondary'
                    )}
                    onClick={handleUsFilter}
                >
                    <Flag
                        className="mr-[12px]"
                        countryCode="US"
                    />
                    <H6>{formatMessage(['discoverPage', 'buttons', 'us'])}</H6>
                </div>
                <div
                    className={cn(
                        'bg-white mr-xs flex h-[42px] cursor-pointer flex-row items-center justify-between border border-border-secondary px-[15px] whitespace-nowrap text-text-secondary transition-all duration-[250ms]',
                        'xsMax:h-[30px] xsMax:px-xs',
                        Boolean(publishDateMin) && 'bg-border-secondary'
                    )}
                    onClick={handleNewFilter}
                >
                    <Sparkle
                        className="mr-[12px] h-[24px] w-[24px]"
                        size="sm"
                        style="fill"
                    />
                    <H6>{formatMessage(['discoverPage', 'buttons', 'new'])}</H6>
                </div>
                <div
                    className={cn(
                        'bg-white mr-xs flex h-[42px] cursor-pointer flex-row items-center justify-between border border-border-secondary px-[15px] whitespace-nowrap text-text-secondary transition-all duration-[250ms]',
                        'xsMax:h-[30px] xsMax:px-xs',
                        Boolean(topRated) && 'bg-border-secondary'
                    )}
                    onClick={handleTopRatedFilter}
                >
                    <img
                        alt="badge"
                        className="mr-[12px] h-[17px] w-[18px]"
                        src="https://p1.liveauctioneers.com/dist/images/badge.svg"
                    />
                    <H6>{formatMessage(['discoverPage', 'buttons', 'top'])}</H6>
                </div>
            </div>
            <div className="mb-[28px] flex h-[70px] flex-row items-center justify-between border-t border-b border-border-secondary">
                <H4>
                    {formatMessage(['search_alerts', 'resultsPlaceholder'], {
                        values: {
                            count: (
                                <FormattedNumber
                                    key="total_records_placeholder"
                                    value={totalRecords}
                                />
                            ),
                        },
                    })}
                </H4>
                <SearchSort
                    archivedSearch={false}
                    instanceId={selectId}
                    sort={sort || DEFAULT_PAGE_SORT}
                    sortOptions={SORT_OPTIONS}
                    sortSelected={handleSortUpdate}
                />
            </div>
        </>
    );
};
